/**
 * Canonical Job BOM Selector
 * ──────────────────────────
 * Single source-of-truth resolver for "what materials does this job need?".
 *
 * Resolution priority (do not reorder):
 *   1. `job.boundJobSpec.snapshot.billOfMaterials` — CANONICAL.
 *      Bound at job creation, immutable thereafter (see src/data/jobSpec.ts
 *      and the JobSpec System memory). This is what the contract was sold on.
 *   2. `job.materialsList` — LEGACY fallback for jobs created before JobSpec
 *      binding existed. Mapped into the same shape.
 *   3. None — `source: "none"`, items: []. UI must show a "BOM not bound —
 *      open job to confirm spec" affordance instead of an empty/skeletal list.
 *
 * Rule (matches Canonical Data Sources policy): consumers MUST NOT read
 * `boundJobSpec`, `materialsList`, or the SKU catalog directly for ordering
 * purposes. Always go through `getJobBom()`.
 */

import type { Job } from "./types";
import type { JobSpecBomItem } from "./jobSpec";
export type JobBomSource = "jobspec" | "materialsList" | "inferred" | "none";

export interface ResolvedJobBom {
  items: JobSpecBomItem[];
  source: JobBomSource;
  /** True when the source is the sold/locked spec — safe to spend on. */
  isCanonical: boolean;
}

const EMPTY: ResolvedJobBom = { items: [], source: "none", isCanonical: false };

export function getJobBom(job: Job): ResolvedJobBom {
  // 1. Canonical: JobSpec snapshot bound at sale.
  const specBom = job.boundJobSpec?.snapshot?.billOfMaterials;
  if (specBom && specBom.length > 0) {
    return { items: specBom, source: "jobspec", isCanonical: true };
  }

  // 2. Legacy: materialsList on the job.
  if (job.materialsList && job.materialsList.length > 0) {
    const items: JobSpecBomItem[] = job.materialsList.map((m) => ({
      sku: m.sku,
      name: m.itemName,
      quantity: m.quantityRequired ?? 1,
    }));
    return { items, source: "materialsList", isCanonical: false };
  }

  // 3. None.
  return EMPTY;
}

/** Short human label for the resolved source — for badges in UI. */
export function getJobBomSourceLabel(source: JobBomSource): string {
  switch (source) {
    case "jobspec":       return "Spec-bound BOM";
    case "materialsList": return "Legacy BOM";
    case "inferred":      return "Inferred BOM — confirm spec";
    case "none":          return "No BOM — confirm spec";
  }
}
